import React from "react";
import { Tag } from "primereact/tag";
import type { ReportContext } from "../../../types/stationPerformance";
import {
  CLOSURE_STATE_LABELS,
  CLOSURE_STATE_SEVERITY,
  COCHARGE_REVENUE_TEXTS,
  cochargeFeeExplanation,
  cochargeFeeLabel,
} from "../StationPerformance.consts";

interface DefinitionContextBannerProps {
  context: ReportContext | null | undefined;
}

/**
 * States every setting the figures on screen were produced under (FR-032, SC-007).
 *
 * Without this, two people reading the same screen can quote the same number and mean different things: * per site or per connector, total or cocharge share, free charging in or out, and computed when. The
 * month's closure state matters most: it tells the reader whether the figure can still move.
 */
const DefinitionContextBanner: React.FC<DefinitionContextBannerProps> = ({ context }) => {
  if (!context) return null;

  const { options, dataCutOff, monthClosure, liveDayIncluded, staleMonths } = context;
  const cutOff = dataCutOff ? new Date(dataCutOff).toLocaleString() : "unknown";

  return (
    <div className="surface-100 border-round p-3 mb-3 text-sm">
      <div className="flex flex-wrap gap-2 align-items-center mb-2">
        <Tag value={options?.unit === "SITE" ? "Per site" : "Per connector"} severity="info" />
        <Tag
          value={
            options?.segment === "ALL"
              ? "All sessions"
              : options?.segment === "PAID_ONLY"
                ? "Paid only"
                : "Free only"
          }
        />
        {options?.moneyView === "COCHARGE_SHARE" ? (
          <>
            <Tag value={COCHARGE_REVENUE_TEXTS.shareLabel} title={COCHARGE_REVENUE_TEXTS.headlineNote} />
            <Tag value={cochargeFeeLabel(context)} title={cochargeFeeExplanation(context?.cochargeFeePercent ?? context?.cochargeSharePercent)} />
          </>
        ) : (
          <Tag value="Total taken" />
        )}
        {options?.includeFreeCharge === false && (
          <Tag value="Free charging excluded" severity="warning" />
        )}
        {options?.station && <Tag value={`Station: ${options.station}`} />}
        {options?.provider && <Tag value={`Channel: ${options.provider}`} />}
      </div>

      <div className="flex flex-wrap gap-2 align-items-center mb-2">
        {Object.entries(monthClosure ?? {}).map(([month, state]) => (
          <Tag
            key={month}
            value={`${month}: ${state}`}
            severity={CLOSURE_STATE_SEVERITY[state] ?? "info"}
            title={CLOSURE_STATE_LABELS[state] ?? state}
          />
        ))}
        {liveDayIncluded && (
          <Tag
            value="Includes today"
            severity="warning"
            title="Today's sessions were folded in live on top of the stored figures"
          />
        )}
      </div>

      <div className="text-color-secondary">
        Computed from data as at <strong>{cutOff}</strong>
        {staleMonths?.length > 0 && (
          <span className="text-orange-600 ml-2">
            · not yet settled: {staleMonths.join(", ")}
          </span>
        )}
      </div>

      <div className="text-color-secondary mt-2">
        Money is cash actually taken. Free charging is measured in kWh and share — no figure here assigns
        it a monetary value, so money understates stations that have shifted to free charging.
      </div>
    </div>
  );
};

export default DefinitionContextBanner;
